// src/services/useAIAgentStore.js

import { create } from "zustand";
import { api } from "./api";

export const useAIAgentStore = create((set, get) => ({
  // State
  profile: null,
  healthCheck: null,
  conversations: [],
  currentConversation: null,
  conversationStats: null,
  conversationSummaries: [],
  workspaceMetrics: null,
  dailyMetrics: [],
  metricsTrends: null,
  tokenUsage: null,
  pendingEscalations: [],
  pendingLeads: [],
  products: [],
  services: [],
  playbooks: [],
  playbooksWithStages: [],
  loading: false,
  error: null,

  // Fetch profile
  fetchProfile: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get("/api/ai-agent/profile");
      set({ profile: response.data.profile, loading: false });
      return response.data.profile;
    } catch (error) {
      if (error.response?.status === 404) {
        set({ profile: null, loading: false });
        return null;
      }
      set({
        error: error.response?.data?.error || "Failed to fetch AI Agent profile",
        loading: false,
      });
      throw error;
    }
  },

  // Fetch health check - mostra status completo do que impede o agente de funcionar
  fetchHealthCheck: async () => {
    try {
      const response = await api.get("/api/ai-agent/health-check");
      set({ healthCheck: response.data });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch health check",
      });
      throw error;
    }
  },

  // Create profile
  createProfile: async (profileData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post("/api/ai-agent/profile", profileData);
      set({ profile: response.data.profile, loading: false });
      return response.data.profile;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to create AI Agent",
        loading: false,
      });
      throw error;
    }
  },

  // Update profile
  updateProfile: async (profileData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.put("/api/ai-agent/profile", profileData);
      set({ profile: response.data.profile, loading: false });
      return response.data.profile;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to update AI Agent",
        loading: false,
      });
      throw error;
    }
  },

  // Delete profile
  deleteProfile: async () => {
    set({ loading: true, error: null });
    try {
      await api.delete("/api/ai-agent/profile");
      set({ profile: null, loading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to delete AI Agent",
        loading: false,
      });
      throw error;
    }
  },

  // Fetch conversations
  fetchConversations: async (status = null) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);

      const response = await api.get(`/api/ai-agent/conversations?${params}`);
      set({ conversations: response.data.conversations, loading: false });
      return response.data.conversations;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch conversations",
        loading: false,
      });
      throw error;
    }
  },

  // Fetch single conversation
  fetchConversation: async (conversationId) => {
    set({ loading: true, error: null });
    try {
      const response = await api.get(`/api/ai-agent/conversations/${conversationId}`);
      set({ currentConversation: response.data, loading: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch conversation",
        loading: false,
      });
      throw error;
    }
  },

  // Escalate conversation
  escalateConversation: async (conversationId, userId, reason) => {
    try {
      await api.post(`/api/ai-agent/conversations/${conversationId}/escalate`, {
        user_id: userId,
        reason,
      });
      // Refresh conversations
      await get().fetchConversations();
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to escalate conversation",
      });
      throw error;
    }
  },

  // Fetch conversation stats (token usage, summary info)
  fetchConversationStats: async (conversationId) => {
    try {
      const response = await api.get(`/api/ai-agent/conversations/${conversationId}/stats`);
      set({ conversationStats: response.data.stats });
      return response.data.stats;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch conversation stats",
      });
      throw error;
    }
  },

  // Fetch conversation summaries
  fetchConversationSummaries: async (conversationId, limit = 10) => {
    try {
      const response = await api.get(`/api/ai-agent/conversations/${conversationId}/summaries?limit=${limit}`);
      set({ conversationSummaries: response.data.summaries });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch summaries",
      });
      throw error;
    }
  },

  // Generate summary for conversation
  generateSummary: async (conversationId, asyncMode = true) => {
    try {
      const response = await api.post(`/api/ai-agent/conversations/${conversationId}/summaries`, {
        async: asyncMode,
      });
      if (!asyncMode && response.data.summary) {
        // Refresh summaries
        await get().fetchConversationSummaries(conversationId);
      }
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to generate summary",
      });
      throw error;
    }
  },

  // Fetch workspace metrics
  fetchWorkspaceMetrics: async () => {
    try {
      const response = await api.get("/api/ai-agent/metrics");
      set({ workspaceMetrics: response.data });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch metrics",
      });
      throw error;
    }
  },

  // Validate API key
  validateApiKey: async (apiKey) => {
    try {
      const response = await api.post("/api/ai-agent/validate-api-key", {
        api_key: apiKey,
      });
      return response.data.valid;
    } catch (error) {
      return false;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // ==================== Products ====================

  // Fetch products
  fetchProducts: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get("/api/ai-agent/products");
      set({ products: response.data.products, loading: false });
      return response.data.products;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch products",
        loading: false,
      });
      throw error;
    }
  },

  // Create product
  createProduct: async (productData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post("/api/ai-agent/products", productData);
      const products = [...get().products, response.data.product];
      set({ products, loading: false });
      return response.data.product;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to create product",
        loading: false,
      });
      throw error;
    }
  },

  // Update product
  updateProduct: async (productId, productData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.put(`/api/ai-agent/products/${productId}`, productData);
      const products = get().products.map((p) =>
        p.id === productId ? response.data.product : p
      );
      set({ products, loading: false });
      return response.data.product;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to update product",
        loading: false,
      });
      throw error;
    }
  },

  // Delete product
  deleteProduct: async (productId) => {
    set({ loading: true, error: null });
    try {
      await api.delete(`/api/ai-agent/products/${productId}`);
      const products = get().products.filter((p) => p.id !== productId);
      set({ products, loading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to delete product",
        loading: false,
      });
      throw error;
    }
  },

  // ==================== Services ====================

  // Fetch services
  fetchServices: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get("/api/ai-agent/services");
      set({ services: response.data.services, loading: false });
      return response.data.services;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch services",
        loading: false,
      });
      throw error;
    }
  },

  // Create service
  createService: async (serviceData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post("/api/ai-agent/services", serviceData);
      const services = [...get().services, response.data.service];
      set({ services, loading: false });
      return response.data.service;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to create service",
        loading: false,
      });
      throw error;
    }
  },

  // Update service
  updateService: async (serviceId, serviceData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.put(`/api/ai-agent/services/${serviceId}`, serviceData);
      const services = get().services.map((s) =>
        s.id === serviceId ? response.data.service : s
      );
      set({ services, loading: false });
      return response.data.service;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to update service",
        loading: false,
      });
      throw error;
    }
  },

  // Delete service
  deleteService: async (serviceId) => {
    set({ loading: true, error: null });
    try {
      await api.delete(`/api/ai-agent/services/${serviceId}`);
      const services = get().services.filter((s) => s.id !== serviceId);
      set({ services, loading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to delete service",
        loading: false,
      });
      throw error;
    }
  },

  // ==================== Playbooks ====================

  // Fetch playbooks with stages
  fetchPlaybooksWithStages: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get("/api/ai-agent/playbooks/with-stages");
      set({ playbooksWithStages: response.data.stages, loading: false });
      return response.data.stages;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch playbooks",
        loading: false,
      });
      throw error;
    }
  },

  // Fetch playbooks
  fetchPlaybooks: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get("/api/ai-agent/playbooks");
      set({ playbooks: response.data.playbooks, loading: false });
      return response.data.playbooks;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch playbooks",
        loading: false,
      });
      throw error;
    }
  },

  // Upsert playbook by stage (create or update)
  upsertPlaybook: async (stageId, playbookData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.put(`/api/ai-agent/playbooks/stage/${stageId}`, playbookData);
      // Refresh playbooks with stages
      await get().fetchPlaybooksWithStages();
      set({ loading: false });
      return response.data.playbook;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to save playbook",
        loading: false,
      });
      throw error;
    }
  },

  // Delete playbook
  deletePlaybook: async (playbookId) => {
    set({ loading: true, error: null });
    try {
      await api.delete(`/api/ai-agent/playbooks/${playbookId}`);
      await get().fetchPlaybooksWithStages();
      set({ loading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to delete playbook",
        loading: false,
      });
      throw error;
    }
  },

  // ==================== Advanced Metrics ====================

  // Fetch daily metrics
  fetchDailyMetrics: async (days = 30) => {
    try {
      const response = await api.get(`/api/ai-agent/metrics/daily?days=${days}`);
      set({ dailyMetrics: response.data.daily_stats || [] });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch daily metrics",
      });
      throw error;
    }
  },

  // Fetch metrics trends
  fetchMetricsTrends: async (days = 30) => {
    try {
      const response = await api.get(`/api/ai-agent/metrics/trends?days=${days}`);
      set({ metricsTrends: response.data });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch metrics trends",
      });
      throw error;
    }
  },

  // Fetch token usage
  fetchTokenUsage: async (days = 30) => {
    try {
      const response = await api.get(`/api/ai-agent/metrics/tokens?days=${days}`);
      set({ tokenUsage: response.data });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch token usage",
      });
      throw error;
    }
  },

    // Fetch pending escalations
  fetchPendingEscalations: async (limit = 50) => {
    try {
      const safeLimit =
        typeof limit === "number"
          ? limit
          : Number(limit?.target?.value ?? limit?.limit ?? limit) || 50;

      const response = await api.get("/api/ai-agent/escalations/pending", {
        params: { limit: Math.min(safeLimit, 100) },
      });

      set({ pendingEscalations: response.data.escalations || [] });
      return response.data.escalations;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch escalations",
      });
      throw error;
    }
  },


  // Add feedback to conversation
  addConversationFeedback: async (conversationId, rating, notes = null) => {
    try {
      const response = await api.post(`/api/ai-agent/conversations/${conversationId}/feedback`, {
        rating,
        notes,
      });
      // Refresh pending escalations
      await get().fetchPendingEscalations();
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to add feedback",
      });
      throw error;
    }
  },

  // Mark conversation as reviewed
  markConversationReviewed: async (conversationId) => {
    try {
      const response = await api.post(`/api/ai-agent/conversations/${conversationId}/review`);
      // Refresh pending escalations
      await get().fetchPendingEscalations();
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to mark as reviewed",
      });
      throw error;
    }
  },

  // Trigger metrics aggregation
  triggerMetricsAggregation: async (daysBack = 7) => {
    try {
      const response = await api.post("/api/ai-agent/metrics/aggregate", {
        days_back: daysBack,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to trigger aggregation",
      });
      throw error;
    }
  },

  // ==================== Dataset Management ====================

  // Dataset state
  datasetStats: null,
  datasetTags: [],
  datasetExports: [],
  promptVariants: [],
  variantComparison: null,

  // Fetch dataset stats
  fetchDatasetStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.quality_rating) {
        filters.quality_rating.forEach((r) => params.append("quality_rating", r));
      }
      if (filters.training_eligible !== undefined) {
        params.append("training_eligible", filters.training_eligible);
      }
      if (filters.tags) {
        filters.tags.forEach((t) => params.append("tags", t));
      }

      const response = await api.get(`/api/ai-agent/dataset/stats?${params}`);
      set({ datasetStats: response.data });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch dataset stats",
      });
      throw error;
    }
  },

  // Fetch dataset tags
  fetchDatasetTags: async () => {
    try {
      const response = await api.get("/api/ai-agent/dataset/tags");
      set({ datasetTags: response.data.tags || [] });
      return response.data.tags;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch tags",
      });
      throw error;
    }
  },

  // Fetch export history
  fetchDatasetExports: async (limit = 50) => {
    try {
      const response = await api.get(`/api/ai-agent/dataset/exports?limit=${limit}`);
      set({ datasetExports: response.data.exports || [] });
      return response.data.exports;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch exports",
      });
      throw error;
    }
  },

  // Export dataset
  exportDataset: async (filters = {}, includeSystemPrompt = true, asyncMode = false) => {
    try {
      const response = await api.post("/api/ai-agent/dataset/export", {
        filters,
        include_system_prompt: includeSystemPrompt,
        async: asyncMode,
      }, {
        responseType: asyncMode ? "json" : "blob",
      });

      if (asyncMode) {
        return response.data;
      } else {
        // Download file
        const blob = new Blob([response.data], { type: "application/x-jsonlines" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dataset_${Date.now()}.jsonl`;
        a.click();
        window.URL.revokeObjectURL(url);
        return { success: true };
      }
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to export dataset",
      });
      throw error;
    }
  },

  // Curate conversation
  curateConversation: async (conversationId, data) => {
    try {
      const response = await api.post(`/api/ai-agent/conversations/${conversationId}/curate`, data);
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to curate conversation",
      });
      throw error;
    }
  },

  // Bulk curate conversations
  bulkCurateConversations: async (conversationIds, data) => {
    try {
      const response = await api.post("/api/ai-agent/conversations/bulk-curate", {
        conversation_ids: conversationIds,
        ...data,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to bulk curate",
      });
      throw error;
    }
  },

  // Bulk tag conversations
  bulkTagConversations: async (conversationIds, addTags = [], removeTags = []) => {
    try {
      const response = await api.post("/api/ai-agent/conversations/bulk-tag", {
        conversation_ids: conversationIds,
        add_tags: addTags,
        remove_tags: removeTags,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to update tags",
      });
      throw error;
    }
  },

  // Prepare fine-tuning dataset
  prepareDataset: async (autoCurate = true, minQuality = "good") => {
    try {
      const response = await api.post("/api/ai-agent/dataset/prepare", {
        auto_curate: autoCurate,
        min_quality: minQuality,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to prepare dataset",
      });
      throw error;
    }
  },

  // ==================== A/B Testing ====================

  // Fetch prompt variants
  fetchPromptVariants: async (includeInactive = false) => {
    try {
      const response = await api.get(`/api/ai-agent/variants?include_inactive=${includeInactive}`);
      set({ promptVariants: response.data.variants || [] });
      return response.data.variants;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch variants",
      });
      throw error;
    }
  },

  // Create prompt variant
  createPromptVariant: async (variantData) => {
    try {
      const response = await api.post("/api/ai-agent/variants", variantData);
      const variants = [...get().promptVariants, response.data.variant];
      set({ promptVariants: variants });
      return response.data.variant;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to create variant",
      });
      throw error;
    }
  },

  // Update prompt variant
  updatePromptVariant: async (variantId, variantData) => {
    try {
      const response = await api.put(`/api/ai-agent/variants/${variantId}`, variantData);
      const variants = get().promptVariants.map((v) =>
        v.id === variantId ? response.data.variant : v
      );
      set({ promptVariants: variants });
      return response.data.variant;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to update variant",
      });
      throw error;
    }
  },

  // Delete prompt variant
  deletePromptVariant: async (variantId) => {
    try {
      await api.delete(`/api/ai-agent/variants/${variantId}`);
      const variants = get().promptVariants.filter((v) => v.id !== variantId);
      set({ promptVariants: variants });
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to delete variant",
      });
      throw error;
    }
  },

  // Fetch variant comparison
  fetchVariantComparison: async (days = 30) => {
    try {
      const response = await api.get(`/api/ai-agent/variants/comparison?days=${days}`);
      set({ variantComparison: response.data });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to fetch comparison",
      });
      throw error;
    }
  },

  // Get winning variant
  getWinningVariant: async () => {
    try {
      const response = await api.get("/api/ai-agent/variants/winner");
      return response.data.winner;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to get winner",
      });
      throw error;
    }
  },

  // Promote variant
  promoteVariant: async (variantId) => {
    try {
      const response = await api.post(`/api/ai-agent/variants/${variantId}/promote`);
      // Refresh profile
      await get().fetchProfile();
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to promote variant",
      });
      throw error;
    }
  },

  // Clone variant
  cloneVariant: async (variantId, newName) => {
    try {
      const response = await api.post(`/api/ai-agent/variants/${variantId}/clone`, {
        name: newName,
      });
      const variants = [...get().promptVariants, response.data.variant];
      set({ promptVariants: variants });
      return response.data.variant;
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to clone variant",
      });
      throw error;
    }
  },

  // Reset variant metrics
  resetVariantMetrics: async (variantId) => {
    try {
      await api.post(`/api/ai-agent/variants/${variantId}/reset-metrics`);
      await get().fetchPromptVariants();
    } catch (error) {
      set({
        error: error.response?.data?.error || "Failed to reset metrics",
      });
      throw error;
    }
  },

  // Reset state
  reset: () =>
    set({
      profile: null,
      healthCheck: null,
      conversations: [],
      currentConversation: null,
      conversationStats: null,
      conversationSummaries: [],
      workspaceMetrics: null,
      dailyMetrics: [],
      metricsTrends: null,
      tokenUsage: null,
      pendingEscalations: [],
      pendingLeads: [],
      products: [],
      services: [],
      playbooks: [],
      playbooksWithStages: [],
      datasetStats: null,
      datasetTags: [],
      datasetExports: [],
      promptVariants: [],
      variantComparison: null,
      loading: false,
      error: null,
    }),
}));
